<template>
  <div class="jim-scheduling-container stock-status-page">

    <!-- PAGE HEADER -->
    <div class="page-header-row">
      <div class="page-header-left"></div>

      <h1 class="page-heading">
        INVENTORY STOCK STATUS
      </h1>

      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- ===================================================== -->
      <!-- LEFT-SIDE CONTROLS -->
      <!-- ===================================================== -->
      <div class="left-side-section">

        <div class="control">
          <label>Location</label>

          <select
            v-model="selectedLocation"
            :disabled="loading"
            @change="loadStockStatus"
          >
            <option value="F">Fresno</option>
            <option value="D">Denver</option>
            <option value="K">Knoxville</option>
            <option value="L">Lakeland</option>
          </select>
        </div>

        <div class="control">
          <label>Item Search</label>

          <input
            v-model="searchTerm"
            type="text"
            placeholder="Item or description"
          />
        </div>

        <div class="control">
          <label>Inventory Class</label>

          <select v-model="invClassFilter">
            <option value="">All Classes</option>

            <option
              v-for="value in invClassOptions"
              :key="value"
              :value="value"
            >
              {{ value }}
            </option>
          </select>
        </div>

        <div class="control">
          <label>Item Status</label>

          <select v-model="itemStatusFilter">
            <option value="">All Statuses</option>

            <option
              v-for="value in itemStatusOptions"
              :key="value"
              :value="value"
            >
              {{ value }}
            </option>
          </select>
        </div>

        <label class="checkbox-row">
          <input
            v-model="negativeAvailableOnly"
            type="checkbox"
          />
          Negative Available
        </label>

        <label class="checkbox-row">
          <input
            v-model="negativeProjectedOnly"
            type="checkbox"
          />
          Negative Projected
        </label>

        <label class="checkbox-row">
          <input
            v-model="belowTargetOnly"
            type="checkbox"
          />
          Below Target
        </label>

        <button
          class="plan-it-btn"
          :disabled="loading"
          @click="loadStockStatus"
        >
          {{ loading ? "Loading..." : "Refresh" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="loading"
          @click="clearFilters"
        >
          Clear Filters
        </button>

        <button
          class="plan-it-btn"
          :disabled="loading || sortedRows.length === 0"
          @click="exportCSV"
        >
          Export CSV
        </button>

        <div
          v-if="error"
          class="panel error-box"
        >
          {{ error }}
        </div>

      </div>

      <!-- ===================================================== -->
      <!-- RIGHT-SIDE REPORT -->
      <!-- ===================================================== -->
      <div class="right-side-section">

        <!-- SUMMARY BAR -->
        <div class="summary-bar">

          <div class="summary-item">
            <div class="summary-label">Displayed Items</div>
            <div class="summary-value">
              {{ formatNumber(filteredSummary.itemCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Negative Available</div>
            <div class="summary-value warning-value">
              {{ formatNumber(filteredSummary.negativeAvailableCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Negative Projected</div>
            <div class="summary-value danger-value">
              {{ formatNumber(filteredSummary.negativeProjectedCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Below Target</div>
            <div class="summary-value">
              {{ formatNumber(filteredSummary.belowTargetCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Sales Orders</div>
            <div class="summary-value">
              {{ formatNumber(filteredSummary.salesOrderCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Work Orders</div>
            <div class="summary-value">
              {{ formatNumber(filteredSummary.workOrderCount) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Purchase Orders</div>
            <div class="summary-value">
              {{ formatNumber(filteredSummary.purchaseOrderCount) }}
            </div>
          </div>

        </div>

        <!-- REPORT INFORMATION -->
        <div class="report-info-row">
          <div>
            Location:
            <strong>{{ selectedLocation }}</strong>
          </div>

          <div>
            Showing:
            <strong>{{ sortedRows.length }}</strong>
            of
            <strong>{{ rows.length }}</strong>
            items
          </div>

          <div>
            Calculated:
            <strong>{{ formatDateTime(calculatedAt) }}</strong>
          </div>
        </div>

        <!-- LOADING -->
        <div
          v-if="loading"
          class="panel status-panel"
        >
          Loading inventory stock status...
        </div>

        <!-- EMPTY -->
        <div
          v-else-if="sortedRows.length === 0"
          class="panel status-panel"
        >
          No stock-status rows match the selected filters.
        </div>

        <!-- MAIN TABLE -->
        <div
          v-else
          class="table-scroll-container"
        >
          <table class="stock-status-table">

            <thead>
              <tr>

                <th
                  class="sticky-item-col sortable-header"
                  @click="setSort('item_no')"
                >
                  Item
                  <span class="sort-indicator">
                    {{ sortIndicator("item_no") }}
                  </span>
                </th>

                <th
                  class="description-col sortable-header"
                  @click="setSort('item_desc_1')"
                >
                  Description
                  <span class="sort-indicator">
                    {{ sortIndicator("item_desc_1") }}
                  </span>
                </th>

                <th
                  class="small-col sortable-header"
                  @click="setSort('inv_class')"
                >
                  Class
                  <span class="sort-indicator">
                    {{ sortIndicator("inv_class") }}
                  </span>
                </th>

                <th
                  class="small-col sortable-header"
                  @click="setSort('item_status')"
                >
                  Status
                  <span class="sort-indicator">
                    {{ sortIndicator("item_status") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('qty_on_hand')"
                >
                  QOH
                  <span class="sort-indicator">
                    {{ sortIndicator("qty_on_hand") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('target_qty')"
                >
                  Target
                  <span class="sort-indicator">
                    {{ sortIndicator("target_qty") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('open_demand_qty')"
                >
                  Open Demand
                  <span class="sort-indicator">
                    {{ sortIndicator("open_demand_qty") }}
                  </span>
                </th>

                <th
                  class="count-col sortable-header"
                  @click="setSort('open_sales_order_count')"
                >
                  SOs
                  <span class="sort-indicator">
                    {{ sortIndicator("open_sales_order_count") }}
                  </span>
                </th>

                <th
                  class="date-col sortable-header"
                  @click="setSort('earliest_sales_req_date')"
                >
                  Earliest Sale
                  <span class="sort-indicator">
                    {{ sortIndicator("earliest_sales_req_date") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('open_work_order_qty')"
                >
                  Open WOs
                  <span class="sort-indicator">
                    {{ sortIndicator("open_work_order_qty") }}
                  </span>
                </th>

                <th
                  class="count-col sortable-header"
                  @click="setSort('open_work_order_count')"
                >
                  WOs
                  <span class="sort-indicator">
                    {{ sortIndicator("open_work_order_count") }}
                  </span>
                </th>

                <th
                  class="date-col sortable-header"
                  @click="setSort('earliest_work_order_due_date')"
                >
                  Earliest WO
                  <span class="sort-indicator">
                    {{ sortIndicator("earliest_work_order_due_date") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('open_purchase_order_qty')"
                >
                  Open POs
                  <span class="sort-indicator">
                    {{ sortIndicator("open_purchase_order_qty") }}
                  </span>
                </th>

                <th
                  class="count-col sortable-header"
                  @click="setSort('open_purchase_order_count')"
                >
                  POs
                  <span class="sort-indicator">
                    {{ sortIndicator("open_purchase_order_count") }}
                  </span>
                </th>

                <th
                  class="date-col sortable-header"
                  @click="setSort('earliest_purchase_order_due_date')"
                >
                  Earliest PO
                  <span class="sort-indicator">
                    {{ sortIndicator("earliest_purchase_order_due_date") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('available_qty')"
                >
                  Available
                  <span class="sort-indicator">
                    {{ sortIndicator("available_qty") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('projected_available_qty')"
                >
                  Projected
                  <span class="sort-indicator">
                    {{ sortIndicator("projected_available_qty") }}
                  </span>
                </th>

                <th
                  class="qty-col sortable-header"
                  @click="setSort('below_target_qty')"
                >
                  Below Target
                  <span class="sort-indicator">
                    {{ sortIndicator("below_target_qty") }}
                  </span>
                </th>

              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in sortedRows"
                :key="`${row.loc}-${row.item_no}`"
                :class="stockRowClass(row)"
              >

                <!-- ITEM -->
                <td
                  class="sticky-item-col clickable-cell item-cell"
                  title="Click for item details"
                  @click="openDetailModal(row, 'summary')"
                >
                  {{ row.item_no }}
                </td>

                <!-- DESCRIPTION -->
                <td
                  class="description-col clickable-cell"
                  title="Click for item details"
                  @click="openDetailModal(row, 'summary')"
                >
                  <div>{{ row.item_desc_1 || "" }}</div>

                  <div
                    v-if="row.item_desc_2"
                    class="description-secondary"
                  >
                    {{ row.item_desc_2 }}
                  </div>
                </td>

                <td class="small-col">
                  {{ row.inv_class || "" }}
                </td>

                <td class="small-col">
                  {{ row.item_status || "" }}
                </td>

                <!-- QOH -->
                <td
                  class="qty-col"
                  :class="quantityClass(row.qty_on_hand)"
                >
                  {{ formatQuantity(row.qty_on_hand) }}
                </td>

                <!-- TARGET -->
                <td class="qty-col">
                  {{ formatQuantity(row.target_qty) }}
                </td>

                <!-- OPEN DEMAND -->
                <td
                  class="qty-col"
                  :class="{
                    'clickable-cell': Number(row.open_demand_qty || 0) > 0
                  }"
                  :title="
                    Number(row.open_demand_qty || 0) > 0
                      ? 'Click to view open sales orders'
                      : ''
                  "
                  @click="
                    Number(row.open_demand_qty || 0) > 0 &&
                    openDetailModal(row, 'sales')
                  "
                >
                  {{ formatQuantity(row.open_demand_qty) }}
                </td>

                <!-- SO COUNT -->
                <td
                  class="count-col"
                  :class="{
                    'clickable-cell': Number(row.open_sales_order_count || 0) > 0
                  }"
                  @click="
                    Number(row.open_sales_order_count || 0) > 0 &&
                    openDetailModal(row, 'sales')
                  "
                >
                  {{ formatNumber(row.open_sales_order_count) }}
                </td>

                <td class="date-col">
                  {{ formatDate(row.earliest_sales_req_date) }}
                </td>

                <!-- OPEN WO -->
                <td
                  class="qty-col"
                  :class="{
                    'clickable-cell': Number(row.open_work_order_qty || 0) > 0
                  }"
                  :title="
                    Number(row.open_work_order_qty || 0) > 0
                      ? 'Click to view open work orders'
                      : ''
                  "
                  @click="
                    Number(row.open_work_order_qty || 0) > 0 &&
                    openDetailModal(row, 'work')
                  "
                >
                  {{ formatQuantity(row.open_work_order_qty) }}
                </td>

                <!-- WO COUNT -->
                <td
                  class="count-col"
                  :class="{
                    'clickable-cell': Number(row.open_work_order_count || 0) > 0
                  }"
                  @click="
                    Number(row.open_work_order_count || 0) > 0 &&
                    openDetailModal(row, 'work')
                  "
                >
                  {{ formatNumber(row.open_work_order_count) }}
                </td>

                <td class="date-col">
                  {{ formatDate(row.earliest_work_order_due_date) }}
                </td>

                <!-- OPEN PO -->
                <td
                  class="qty-col"
                  :class="{
                    'clickable-cell': Number(row.open_purchase_order_qty || 0) > 0
                  }"
                  :title="
                    Number(row.open_purchase_order_qty || 0) > 0
                      ? 'Click to view open purchase orders'
                      : ''
                  "
                  @click="
                    Number(row.open_purchase_order_qty || 0) > 0 &&
                    openDetailModal(row, 'purchase')
                  "
                >
                  {{ formatQuantity(row.open_purchase_order_qty) }}
                </td>

                <!-- PO COUNT -->
                <td
                  class="count-col"
                  :class="{
                    'clickable-cell': Number(row.open_purchase_order_count || 0) > 0
                  }"
                  @click="
                    Number(row.open_purchase_order_count || 0) > 0 &&
                    openDetailModal(row, 'purchase')
                  "
                >
                  {{ formatNumber(row.open_purchase_order_count) }}
                </td>

                <td class="date-col">
                  {{ formatDate(row.earliest_purchase_order_due_date) }}
                </td>

                <!-- AVAILABLE -->
                <td
                  class="qty-col strong-qty"
                  :class="quantityClass(row.available_qty)"
                >
                  {{ formatQuantity(row.available_qty) }}
                </td>

                <!-- PROJECTED -->
                <td
                  class="qty-col strong-qty"
                  :class="quantityClass(row.projected_available_qty)"
                >
                  {{ formatQuantity(row.projected_available_qty) }}
                </td>

                <!-- BELOW TARGET -->
                <td
                  class="qty-col"
                  :class="{
                    'below-target-cell': Number(row.below_target_qty || 0) > 0
                  }"
                >
                  {{ formatQuantity(row.below_target_qty) }}
                </td>

              </tr>
            </tbody>

          </table>
        </div>

      </div>

      <!-- ===================================================== -->
      <!-- ITEM DETAIL POPUP -->
      <!-- ===================================================== -->
      <div
        v-if="showDetailModal"
        class="popup-overlay"
        @click.self="closeDetailModal"
      >
        <div class="popup-content detail-popup">

          <div class="popup-header">

            <div class="popup-title-area">
              <div class="popup-title">
                {{ selectedRow?.item_no }}
              </div>

              <div class="popup-subtitle">
                {{ selectedRow?.item_desc_1 }}
                <span v-if="selectedRow?.item_desc_2">
                  — {{ selectedRow.item_desc_2 }}
                </span>
              </div>

              <div class="popup-subtitle">
                Location {{ selectedRow?.loc }}
              </div>
            </div>

            <button
              class="close-btn"
              @click="closeDetailModal"
            >
              ✕
            </button>

          </div>

          <!-- TABS -->
          <div class="popup-tabs">

            <button
              class="popup-tab-btn"
              :class="{ active: activeDetailTab === 'summary' }"
              @click="switchDetailTab('summary')"
            >
              Summary
            </button>

            <button
              class="popup-tab-btn"
              :class="{ active: activeDetailTab === 'sales' }"
              :disabled="Number(selectedRow?.open_sales_order_count || 0) === 0"
              @click="switchDetailTab('sales')"
            >
              Sales Orders
              ({{ formatNumber(selectedRow?.open_sales_order_count) }})
            </button>

            <button
              class="popup-tab-btn"
              :class="{ active: activeDetailTab === 'work' }"
              :disabled="Number(selectedRow?.open_work_order_count || 0) === 0"
              @click="switchDetailTab('work')"
            >
              Work Orders
              ({{ formatNumber(selectedRow?.open_work_order_count) }})
            </button>

            <button
              class="popup-tab-btn"
              :class="{ active: activeDetailTab === 'purchase' }"
              :disabled="Number(selectedRow?.open_purchase_order_count || 0) === 0"
              @click="switchDetailTab('purchase')"
            >
              Purchase Orders
              ({{ formatNumber(selectedRow?.open_purchase_order_count) }})
            </button>

          </div>

          <div
            v-if="detailLoading"
            class="panel status-panel"
          >
            Loading detail...
          </div>

          <div
            v-if="detailError"
            class="panel error-box modal-error"
          >
            {{ detailError }}
          </div>

          <!-- ================================================= -->
          <!-- SUMMARY TAB -->
          <!-- ================================================= -->
          <div
            v-if="
              activeDetailTab === 'summary' &&
              !detailLoading
            "
            class="detail-tab-panel"
          >
            <div class="popup-metrics">

              <div class="metric">
                <div class="metric-key">Quantity On Hand</div>
                <div
                  class="metric-value"
                  :class="quantityClass(selectedRow?.qty_on_hand)"
                >
                  {{ formatQuantity(selectedRow?.qty_on_hand) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Target</div>
                <div class="metric-value">
                  {{ formatQuantity(selectedRow?.target_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Open Demand</div>
                <div class="metric-value">
                  {{ formatQuantity(selectedRow?.open_demand_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Open Work Orders</div>
                <div class="metric-value">
                  {{ formatQuantity(selectedRow?.open_work_order_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Open Purchase Orders</div>
                <div class="metric-value">
                  {{ formatQuantity(selectedRow?.open_purchase_order_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Available</div>
                <div
                  class="metric-value"
                  :class="quantityClass(selectedRow?.available_qty)"
                >
                  {{ formatQuantity(selectedRow?.available_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Projected Available</div>
                <div
                  class="metric-value"
                  :class="quantityClass(selectedRow?.projected_available_qty)"
                >
                  {{ formatQuantity(selectedRow?.projected_available_qty) }}
                </div>
              </div>

              <div class="metric">
                <div class="metric-key">Below Target</div>
                <div
                  class="metric-value"
                  :class="{
                    'negative-qty': Number(selectedRow?.below_target_qty || 0) > 0
                  }"
                >
                  {{ formatQuantity(selectedRow?.below_target_qty) }}
                </div>
              </div>

            </div>

            <div class="formula-panel">
              <div>
                <strong>Available:</strong>
                QOH − Open Demand
              </div>

              <div>
                <strong>Projected:</strong>
                Available + Open Work Orders + Open Purchase Orders
              </div>
            </div>
          </div>

          <!-- ================================================= -->
          <!-- SALES ORDER DETAIL TAB -->
          <!-- ================================================= -->
          <div
            v-if="
              activeDetailTab === 'sales' &&
              !detailLoading &&
              !detailError
            "
            class="detail-tab-panel"
          >
            <div class="detail-summary-line">
              Open demand:
              <strong>
                {{ formatQuantity(selectedRow?.open_demand_qty) }}
              </strong>

              across

              <strong>
                {{ formatNumber(selectedRow?.open_sales_order_count) }}
              </strong>

              sales order(s)
            </div>

            <table class="popup-table">
              <thead>
                <tr>
                  <th>Req Ship</th>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Customer Name</th>
                  <th class="numeric-header">Ordered</th>
                  <th class="numeric-header">Shipped</th>
                  <th class="numeric-header">Remaining</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in salesDetailRows"
                  :key="`${row.ord_no}-${row.item_no}-${row.req_ship_dt}`"
                >
                  <td>{{ formatDate(row.req_ship_dt) }}</td>
                  <td>{{ row.ord_no }}</td>
                  <td>{{ row.customer_id || row.cus_no || "" }}</td>
                  <td>{{ row.customer_name || row.ship_to_name || "" }}</td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.qty_ordered) }}
                  </td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.qty_shipped) }}
                  </td>

                  <td class="numeric-cell">
                    {{
                      formatQuantity(
                        row.remaining_qty ??
                        row.qty_to_ship ??
                        row.qty_ordered
                      )
                    }}
                  </td>
                </tr>

                <tr v-if="salesDetailRows.length === 0">
                  <td
                    colspan="7"
                    class="empty-row"
                  >
                    No open sales-order rows found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ================================================= -->
          <!-- WORK ORDER DETAIL TAB -->
          <!-- ================================================= -->
          <div
            v-if="
              activeDetailTab === 'work' &&
              !detailLoading &&
              !detailError
            "
            class="detail-tab-panel"
          >
            <div class="detail-summary-line">
              Remaining production:
              <strong>
                {{ formatQuantity(selectedRow?.open_work_order_qty) }}
              </strong>

              across

              <strong>
                {{ formatNumber(selectedRow?.open_work_order_count) }}
              </strong>

              work order(s)
            </div>

            <table class="popup-table">
              <thead>
                <tr>
                  <th>Due Date</th>
                  <th>Work Order</th>
                  <th>Machine</th>
                  <th class="numeric-header">Original Qty</th>
                  <th class="numeric-header">Reported Qty</th>
                  <th class="numeric-header">Remaining Qty</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in workOrderDetailRows"
                  :key="row.ord_no"
                >
                  <td>{{ formatDate(row.due_dt) }}</td>
                  <td>{{ row.ord_no }}</td>
                  <td>{{ row.machine || "" }}</td>

                  <td class="numeric-cell">
                    {{
                      formatQuantity(
                        row.original_qty ??
                        row.ord_qty_original ??
                        (
                          Number(row.remaining_qty || row.open_qty || 0) +
                          Number(row.report_qty || 0)
                        )
                      )
                    }}
                  </td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.report_qty) }}
                  </td>

                  <td class="numeric-cell">
                    {{
                      formatQuantity(
                        row.remaining_qty ??
                        row.open_qty ??
                        row.ord_qty
                      )
                    }}
                  </td>
                </tr>

                <tr v-if="workOrderDetailRows.length === 0">
                  <td
                    colspan="6"
                    class="empty-row"
                  >
                    No open work-order rows found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ================================================= -->
          <!-- PURCHASE ORDER DETAIL TAB -->
          <!-- ================================================= -->
          <div
            v-if="
              activeDetailTab === 'purchase' &&
              !detailLoading &&
              !detailError
            "
            class="detail-tab-panel"
          >
            <div class="detail-summary-line">
              Open purchase quantity:
              <strong>
                {{ formatQuantity(selectedRow?.open_purchase_order_qty) }}
              </strong>

              across

              <strong>
                {{ formatNumber(selectedRow?.open_purchase_order_count) }}
              </strong>

              purchase order(s)
            </div>

            <table class="popup-table">
              <thead>
                <tr>
                  <th>Due Date</th>
                  <th>Purchase Order</th>
                  <th>Line</th>
                  <th>Vendor</th>
                  <th class="numeric-header">Ordered</th>
                  <th class="numeric-header">Received</th>
                  <th class="numeric-header">Cancelled</th>
                  <th class="numeric-header">Open Qty</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in purchaseOrderDetailRows"
                  :key="`${row.ord_no}-${row.line_no}`"
                >
                  <td>{{ formatDate(row.due_dt) }}</td>
                  <td>{{ row.ord_no }}</td>
                  <td>{{ row.line_no ?? "" }}</td>
                  <td>{{ row.vend_no || row.vendor_no || "" }}</td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.qty_ordered) }}
                  </td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.qty_received) }}
                  </td>

                  <td class="numeric-cell">
                    {{ formatQuantity(row.qty_cancelled) }}
                  </td>

                  <td class="numeric-cell">
                    {{
                      formatQuantity(
                        row.open_qty ??
                        row.remaining_qty
                      )
                    }}
                  </td>
                </tr>

                <tr v-if="purchaseOrderDetailRows.length === 0">
                  <td
                    colspan="8"
                    class="empty-row"
                  >
                    No open purchase-order rows found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "InventoryStockStatus",

  data() {
    return {
      selectedLocation: "D",

      loading: false,
      error: null,

      rows: [],
      summary: {},
      calculatedAt: null,

      searchTerm: "",
      invClassFilter: "",
      itemStatusFilter: "",

      negativeAvailableOnly: false,
      negativeProjectedOnly: false,
      belowTargetOnly: false,

      sortKey: "item_no",
      sortDir: "asc",

      showDetailModal: false,
      selectedRow: null,
      activeDetailTab: "summary",

      detailLoading: false,
      detailError: null,

      salesDetailRows: [],
      workOrderDetailRows: [],
      purchaseOrderDetailRows: []
    };
  },

  computed: {
    invClassOptions() {
      return [
        ...new Set(
          this.rows
            .map(row => String(row.inv_class || "").trim())
            .filter(Boolean)
        )
      ].sort();
    },

    itemStatusOptions() {
      return [
        ...new Set(
          this.rows
            .map(row => String(row.item_status || "").trim())
            .filter(Boolean)
        )
      ].sort();
    },

    filteredRows() {
      const search = String(this.searchTerm || "")
        .trim()
        .toLowerCase();

      return this.rows.filter(row => {
        const itemNo = String(row.item_no || "").toLowerCase();
        const description1 = String(row.item_desc_1 || "").toLowerCase();
        const description2 = String(row.item_desc_2 || "").toLowerCase();

        if (
          search &&
          !itemNo.includes(search) &&
          !description1.includes(search) &&
          !description2.includes(search)
        ) {
          return false;
        }

        if (
          this.invClassFilter &&
          String(row.inv_class || "").trim() !== this.invClassFilter
        ) {
          return false;
        }

        if (
          this.itemStatusFilter &&
          String(row.item_status || "").trim() !== this.itemStatusFilter
        ) {
          return false;
        }

        if (
          this.negativeAvailableOnly &&
          Number(row.available_qty || 0) >= 0
        ) {
          return false;
        }

        if (
          this.negativeProjectedOnly &&
          Number(row.projected_available_qty || 0) >= 0
        ) {
          return false;
        }

        if (
          this.belowTargetOnly &&
          Number(row.below_target_qty || 0) <= 0
        ) {
          return false;
        }

        return true;
      });
    },

    sortedRows() {
      const rows = [...this.filteredRows];

      const numericFields = new Set([
        "qty_on_hand",
        "target_qty",
        "open_demand_qty",
        "open_sales_order_count",
        "open_work_order_qty",
        "open_work_order_count",
        "open_purchase_order_qty",
        "open_purchase_order_count",
        "open_supply_qty",
        "available_qty",
        "projected_available_qty",
        "below_target_qty"
      ]);

      const dateFields = new Set([
        "earliest_sales_req_date",
        "earliest_work_order_due_date",
        "earliest_purchase_order_due_date",
        "calculated_at",
        "updated_at"
      ]);

      const key = this.sortKey;
      const direction = this.sortDir === "asc" ? 1 : -1;

      rows.sort((a, b) => {
        const aValue = a?.[key];
        const bValue = b?.[key];

        if (numericFields.has(key)) {
          return (
            Number(aValue || 0) -
            Number(bValue || 0)
          ) * direction;
        }

        if (dateFields.has(key)) {
          const aTime = aValue
            ? new Date(aValue).getTime()
            : 0;

          const bTime = bValue
            ? new Date(bValue).getTime()
            : 0;

          return (aTime - bTime) * direction;
        }

        return String(aValue ?? "")
          .localeCompare(
            String(bValue ?? ""),
            undefined,
            {
              numeric: true,
              sensitivity: "base"
            }
          ) * direction;
      });

      return rows;
    },

    filteredSummary() {
      const rows = this.filteredRows;

      return {
        itemCount: rows.length,

        negativeAvailableCount: rows.filter(
          row => Number(row.available_qty || 0) < 0
        ).length,

        negativeProjectedCount: rows.filter(
          row => Number(row.projected_available_qty || 0) < 0
        ).length,

        belowTargetCount: rows.filter(
          row => Number(row.below_target_qty || 0) > 0
        ).length,

        salesOrderCount: rows.reduce(
          (total, row) =>
            total + Number(row.open_sales_order_count || 0),
          0
        ),

        workOrderCount: rows.reduce(
          (total, row) =>
            total + Number(row.open_work_order_count || 0),
          0
        ),

        purchaseOrderCount: rows.reduce(
          (total, row) =>
            total + Number(row.open_purchase_order_count || 0),
          0
        )
      };
    }
  },

  mounted() {
    const savedLocation = localStorage.getItem(
      "inventoryStockStatusLocation"
    );

    if (["F", "D", "K", "L"].includes(savedLocation)) {
      this.selectedLocation = savedLocation;
    }

    this.loadStockStatus();
  },

  methods: {
    async loadStockStatus() {
      this.loading = true;
      this.error = null;

      try {
        const location = String(
          this.selectedLocation || "D"
        )
          .trim()
          .toUpperCase();

        localStorage.setItem(
          "inventoryStockStatusLocation",
          location
        );

        const { data } = await axios.get(
          "/inventory-stock-status",
          {
            params: {
              location
            }
          }
        );

        this.rows = Array.isArray(data?.rows)
          ? data.rows
          : [];

        this.summary = data?.summary || {};

        this.calculatedAt =
          data?.calculated_at ||
          data?.summary?.calculated_at ||
          null;

      } catch (error) {
        console.error(
          "loadStockStatus error:",
          error
        );

        this.rows = [];
        this.summary = {};
        this.calculatedAt = null;

        this.error =
          error?.response?.data?.error ||
          error?.response?.data?.details ||
          "Failed to load inventory stock status.";
      } finally {
        this.loading = false;
      }
    },

    clearFilters() {
      this.searchTerm = "";
      this.invClassFilter = "";
      this.itemStatusFilter = "";

      this.negativeAvailableOnly = false;
      this.negativeProjectedOnly = false;
      this.belowTargetOnly = false;

      this.sortKey = "item_no";
      this.sortDir = "asc";
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir =
          this.sortDir === "asc"
            ? "desc"
            : "asc";
      } else {
        this.sortKey = key;

        const descendingByDefault = [
          "qty_on_hand",
          "target_qty",
          "open_demand_qty",
          "open_sales_order_count",
          "open_work_order_qty",
          "open_work_order_count",
          "open_purchase_order_qty",
          "open_purchase_order_count",
          "available_qty",
          "projected_available_qty",
          "below_target_qty"
        ];

        this.sortDir =
          descendingByDefault.includes(key)
            ? "desc"
            : "asc";
      }
    },

    sortIndicator(key) {
      if (this.sortKey !== key) {
        return "";
      }

      return this.sortDir === "asc"
        ? "▲"
        : "▼";
    },

    stockRowClass(row) {
      return {
        "negative-projected-row":
          Number(row.projected_available_qty || 0) < 0,

        "negative-available-row":
          Number(row.available_qty || 0) < 0 &&
          Number(row.projected_available_qty || 0) >= 0,

        "below-target-row":
          Number(row.below_target_qty || 0) > 0 &&
          Number(row.available_qty || 0) >= 0
      };
    },

    quantityClass(value) {
      const number = Number(value || 0);

      return {
        "negative-qty": number < 0,
        "positive-qty": number > 0
      };
    },

    openDetailModal(row, tab = "summary") {
      this.selectedRow = row;
      this.activeDetailTab = tab;

      this.salesDetailRows = [];
      this.workOrderDetailRows = [];
      this.purchaseOrderDetailRows = [];

      this.detailError = null;
      this.detailLoading = false;
      this.showDetailModal = true;

      if (tab !== "summary") {
        this.loadDetailRows(tab);
      }
    },

    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedRow = null;
      this.activeDetailTab = "summary";

      this.detailLoading = false;
      this.detailError = null;

      this.salesDetailRows = [];
      this.workOrderDetailRows = [];
      this.purchaseOrderDetailRows = [];
    },

    async switchDetailTab(tab) {
      this.activeDetailTab = tab;
      this.detailError = null;

      if (tab === "summary") {
        return;
      }

      const alreadyLoaded =
        (
          tab === "sales" &&
          this.salesDetailRows.length > 0
        ) ||
        (
          tab === "work" &&
          this.workOrderDetailRows.length > 0
        ) ||
        (
          tab === "purchase" &&
          this.purchaseOrderDetailRows.length > 0
        );

      if (!alreadyLoaded) {
        await this.loadDetailRows(tab);
      }
    },

    async loadDetailRows(tab) {
      if (!this.selectedRow?.item_no) {
        return;
      }

      const routeMap = {
        sales:
          "/inventory-stock-status/sales-orders",

        work:
          "/inventory-stock-status/work-orders",

        purchase:
          "/inventory-stock-status/purchase-orders"
      };

      const route = routeMap[tab];

      if (!route) {
        return;
      }

      this.detailLoading = true;
      this.detailError = null;

      try {
        const { data } = await axios.get(
          route,
          {
            params: {
              location: this.selectedRow.loc,
              item_no: this.selectedRow.item_no
            }
          }
        );

        const detailRows = Array.isArray(data?.rows)
          ? data.rows
          : [];

        if (tab === "sales") {
          this.salesDetailRows = detailRows;
        }

        if (tab === "work") {
          this.workOrderDetailRows = detailRows;
        }

        if (tab === "purchase") {
          this.purchaseOrderDetailRows = detailRows;
        }

      } catch (error) {
        console.error(
          `loadDetailRows ${tab} error:`,
          error
        );

        if (error?.response?.status === 404) {
          this.detailError =
            "The detail route has not been added yet. " +
            "The stock-status summary remains valid.";
        } else {
          this.detailError =
            error?.response?.data?.error ||
            error?.response?.data?.details ||
            "Failed to load stock-status detail.";
        }
      } finally {
        this.detailLoading = false;
      }
    },

    formatQuantity(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "0";
      }

      const hasDecimals =
        Math.abs(number % 1) > 0.000001;

      return number.toLocaleString(
        undefined,
        {
          minimumFractionDigits:
            hasDecimals ? 2 : 0,

          maximumFractionDigits: 4
        }
      );
    },

    formatNumber(value) {
      const number = Number(value);

      if (!Number.isFinite(number)) {
        return "0";
      }

      return number.toLocaleString();
    },

    formatDate(value) {
      if (!value) {
        return "";
      }

      const text = String(value);

      const date = /^\d{4}-\d{2}-\d{2}$/.test(text)
        ? new Date(`${text}T00:00:00`)
        : new Date(value);

      if (Number.isNaN(date.getTime())) {
        return text;
      }

      return date.toLocaleDateString();
    },

    formatDateTime(value) {
      if (!value) {
        return "Not available";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },

    escapeCSV(value) {
      return `"${String(value ?? "")
        .replace(/"/g, '""')}"`;
    },

    exportCSV() {
      if (this.sortedRows.length === 0) {
        return;
      }

      const columns = [
        ["Location", "loc"],
        ["Item", "item_no"],
        ["Description 1", "item_desc_1"],
        ["Description 2", "item_desc_2"],
        ["Inventory Class", "inv_class"],
        ["Status", "item_status"],
        ["Material Cost Type", "mat_cost_type"],
        ["Quantity On Hand", "qty_on_hand"],
        ["Target", "target_qty"],
        ["Open Demand", "open_demand_qty"],
        ["Sales Order Count", "open_sales_order_count"],
        ["Earliest Sales Date", "earliest_sales_req_date"],
        ["Open Work Order Qty", "open_work_order_qty"],
        ["Work Order Count", "open_work_order_count"],
        ["Earliest Work Order Date", "earliest_work_order_due_date"],
        ["Open Purchase Order Qty", "open_purchase_order_qty"],
        ["Purchase Order Count", "open_purchase_order_count"],
        ["Earliest Purchase Order Date", "earliest_purchase_order_due_date"],
        ["Open Supply", "open_supply_qty"],
        ["Available", "available_qty"],
        ["Projected Available", "projected_available_qty"],
        ["Below Target", "below_target_qty"],
        ["Calculated At", "calculated_at"]
      ];

      const output = [];

      output.push(
        columns
          .map(column => this.escapeCSV(column[0]))
          .join(",")
      );

      for (const row of this.sortedRows) {
        output.push(
          columns
            .map(column =>
              this.escapeCSV(row[column[1]])
            )
            .join(",")
        );
      }

      const csv = output.join("\n");

      const blob = new Blob(
        [csv],
        {
          type: "text/csv;charset=utf-8;"
        }
      );

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        `inventory_stock_status_${this.selectedLocation}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>

/* ============================================================
   PAGE BASE
============================================================ */

.stock-status-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* ============================================================
   HEADER
============================================================ */

.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: "Cormorant Garamond", Garamond, serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* ============================================================
   MAIN LAYOUT
============================================================ */

.main-content {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 80px;
}

.right-side-section {
  width: 100%;
  min-width: 0;
}

/* ============================================================
   CONTROLS
============================================================ */

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.control select {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-size: 14px;
  color: #cbbfbf;
  cursor: pointer;
}

.checkbox-row input {
  cursor: pointer;
}

/* ============================================================
   BUTTONS
============================================================ */

.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* ============================================================
   PANELS
============================================================ */

.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
  box-sizing: border-box;
}

.modal-error {
  margin-top: 10px;
}

.status-panel {
  text-align: center;
  font-weight: bold;
}

/* ============================================================
   SUMMARY
============================================================ */

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 10px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 115px;
  flex: 1 1 115px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 14px;
  opacity: 0.9;
  line-height: 1.1;
}

.summary-value {
  font-size: 22px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 3px;
}

.warning-value {
  color: #fde68a;
}

.danger-value {
  color: #fca5a5;
}

.report-info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  margin-bottom: 8px;
  padding: 0 4px;
}

/* ============================================================
   MAIN TABLE
============================================================ */

.table-scroll-container {
  max-height: calc(100vh - 190px);
  overflow: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  box-sizing: border-box;
  border: 1px solid #444;
}

.table-scroll-container::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

.stock-status-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1900px;
}

.stock-status-table th,
.stock-status-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  white-space: nowrap;
}

.stock-status-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 5;
  text-align: center;
}

.stock-status-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: #1a1a1a;
}

.sort-indicator {
  display: inline-block;
  min-width: 11px;
  margin-left: 4px;
  font-size: 10px;
}

.sticky-item-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  min-width: 125px;
  text-align: left;
}

.stock-status-table th.sticky-item-col {
  z-index: 8;
}

.description-col {
  min-width: 220px;
  max-width: 280px;
  white-space: normal !important;
  text-align: left;
}

.description-secondary {
  font-size: 11px;
  opacity: 0.75;
  margin-top: 2px;
}

.small-col {
  min-width: 55px;
  text-align: center;
}

.qty-col {
  min-width: 88px;
  text-align: right;
}

.count-col {
  min-width: 50px;
  text-align: right;
}

.date-col {
  min-width: 90px;
  text-align: center;
}

.strong-qty {
  font-weight: bold;
}

.clickable-cell {
  cursor: pointer;
}

.clickable-cell:hover {
  text-decoration: underline;
}

.item-cell {
  font-weight: bold;
}

.negative-qty {
  color: #ff8d8d !important;
  font-weight: bold;
}

.positive-qty {
  color: #d1fae5;
}

.below-target-cell {
  color: #fde68a;
  font-weight: bold;
}

.negative-projected-row td {
  background-color: rgba(127, 29, 29, 0.28);
}

.negative-available-row td {
  background-color: rgba(120, 53, 15, 0.24);
}

.below-target-row td {
  background-color: rgba(113, 63, 18, 0.15);
}

/* Keep sticky item cell aligned with row coloring */
.negative-projected-row .sticky-item-col {
  background-color: #321010;
}

.negative-available-row .sticky-item-col {
  background-color: #2a1b09;
}

.below-target-row .sticky-item-col {
  background-color: #211a0b;
}

/* ============================================================
   POPUP
============================================================ */

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.68);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 14px 18px;
  border-radius: 8px;
  text-align: left;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

.detail-popup {
  width: min(1150px, 95vw);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.popup-title-area {
  text-align: left;
}

.popup-title {
  font-family: "Cormorant Garamond", Garamond, serif;
  font-size: 25px;
  font-weight: bold;
  color: #fff;
}

.popup-subtitle {
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

.popup-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.popup-tab-btn {
  border-radius: 10px;
  padding: 6px 12px;
  background-color: #111;
  border: 1px solid #444;
  color: #cbbfbf;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
}

.popup-tab-btn.active {
  background-color: #708090a4;
  border-color: #888;
  color: #fff;
}

.popup-tab-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.detail-tab-panel {
  margin-top: 10px;
}

.popup-metrics {
  display: grid;
  grid-template-columns: repeat(
    4,
    minmax(145px, 1fr)
  );
  gap: 10px;
}

.metric {
  border: 1px solid #333;
  border-radius: 8px;
  padding: 10px;
  background: #0f141c;
}

.metric-key {
  font-size: 12px;
  opacity: 0.85;
}

.metric-value {
  font-size: 20px;
  font-weight: bold;
  margin-top: 3px;
  color: #fff;
}

.formula-panel {
  border: 1px solid #333;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 12px;
  background: #0f141c;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-summary-line {
  margin-bottom: 8px;
  font-size: 14px;
}

/* ============================================================
   POPUP TABLES
============================================================ */

.popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.popup-table th,
.popup-table td {
  border: 1px solid #333;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}

.popup-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
}

.popup-table tr:nth-child(even) td {
  background: #0f141c;
}

.numeric-header,
.numeric-cell {
  text-align: right !important;
}

.empty-row {
  text-align: center !important;
  opacity: 0.8;
  padding: 14px !important;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (max-width: 1000px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .left-side-section {
    margin-top: 0;
    display: grid;
    grid-template-columns: repeat(
      2,
      minmax(150px, 1fr)
    );
    align-items: end;
  }

  .plan-it-btn {
    width: 100%;
  }

  .popup-metrics {
    grid-template-columns: repeat(
      2,
      minmax(145px, 1fr)
    );
  }
}

@media (max-width: 600px) {
  .left-side-section {
    grid-template-columns: 1fr;
  }

  .popup-metrics {
    grid-template-columns: 1fr;
  }

  .page-heading {
    font-size: 28px;
  }
}

</style>